import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { IoAdd, IoClose, IoCopyOutline } from "react-icons/io5";
import { MdOutlineGroups } from "react-icons/md";
import CustomNavbar from "../../components/common/CustomNavbar";
import PageLayout from "../../components/layouts/PageLayout";
import ECSLoader from "../../components/common/EcsLoader";
import customFetch from "../../util/config/axiosConfig";
import { getTeamRole } from "../../util/manageAuth";

/** GET /api/team/members */
interface Member {
  userId: number;
  fullName?: string | null;
  email?: string | null;
  mobile?: string | null;
  role: "OWNER" | "ADMIN" | "AGENT";
  seat?: string | null;
  status?: string | null;
  lastSeenAt?: number | null;
  device?: string | null;
  temporaryPassword?: string | null;
}

/** GET /api/team/seats */
interface Seats {
  used: number;
  included: number;
  extra: number;
  total: number;
  free: number;
  planKey?: string | null;
  planTitle?: string | null;
  extraSeatPrice?: number | null;
  canBuySeats?: boolean;
  enforced?: boolean;
}

/** Error codes the backend uses for seats. */
const NO_FREE_SEAT = 4030;

const apiError = (e: any): { code?: number; message: string } => ({
  code: e?.response?.data?.error?.code,
  message: e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || "Something went wrong",
});

const INPUT =
  "w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand-main";
const BTN_MAIN =
  "inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-4xl bg-brand-blue-gradient text-white font-bold text-sm disabled:opacity-50";
const BTN_PLAIN =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-4xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50";

const ROLE_STYLE: Record<string, string> = {
  OWNER: "bg-purple-50 text-purple-700 border-purple-100",
  ADMIN: "bg-blue-50 text-blue-700 border-blue-100",
  AGENT: "bg-gray-50 text-gray-700 border-gray-200",
};

const STATUS_STYLE: Record<string, string> = {
  ONLINE: "bg-green-500",
  AWAY: "bg-gray-300",
  INVITED: "bg-yellow-400",
};

function since(ms?: number | null) {
  if (!ms) return "Never";
  const m = Math.round((Date.now() - ms) / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m} min ago`;
  if (m < 60 * 24) return `${Math.round(m / 60)} h ago`;
  return new Date(ms).toLocaleDateString();
}

/**
 * The company's team (AiSensy style): the owner, admins and agents, the plan's seats, and adding members —
 * either by creating their account with a temporary password, or by adding someone who already signed up.
 */
export default function Team() {
  const isOwner = getTeamRole() === "OWNER";
  const [members, setMembers] = useState<Member[] | null>(null);
  const [seats, setSeats] = useState<Seats | null>(null);
  const [adding, setAdding] = useState(false);
  const [shown, setShown] = useState<{ member: Member; password: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([customFetch.get("/api/team/members"), customFetch.get("/api/team/seats")]);
      setMembers(m.data);
      setSeats(s.data);
    } catch (e) {
      toast.error(apiError(e).message);
      setMembers([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function setRole(m: Member, role: string) {
    setBusyId(m.userId);
    try {
      await customFetch.put(`/api/team/employees/${m.userId}/role`, { role });
      toast.success(`${m.fullName || "Member"} is now ${role === "ADMIN" ? "an admin" : "an agent"}`);
      await load();
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  async function resetPassword(m: Member) {
    if (!window.confirm(`Give ${m.fullName || m.email} a new temporary password? Their current password stops working.`)) return;
    setBusyId(m.userId);
    try {
      const res = await customFetch.post(`/api/team/employees/${m.userId}/reset-password`);
      setShown({ member: res.data, password: res.data.temporaryPassword });
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  async function remove(m: Member) {
    if (!window.confirm(`Remove ${m.fullName || m.email} from your team? They won't be able to sign in to your account.`)) return;
    setBusyId(m.userId);
    try {
      await customFetch.delete(`/api/team/employees/${m.userId}`);
      toast.success("Member removed");
      await load();
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <PageLayout
      customNavbar={
        <CustomNavbar>
          <div className="flex gap-3 items-center w-max">
            <span className="font-bold text-lg uppercase">Team</span>
          </div>
        </CustomNavbar>
      }
    >
      <div className="bg-white rounded-4xl p-7 flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Team members</h2>
            <p className="text-sm text-gray-400 mt-1">
              Admins can do everything. Agents handle chats, contacts and sending, but not billing, team, API keys or
              WhatsApp number settings.
            </p>
          </div>
          <button className={BTN_MAIN} onClick={() => setAdding(true)}>
            <IoAdd className="text-lg" /> Add member
          </button>
        </div>

        {seats && (
          <div className="rounded-2xl border border-gray-100 bg-gray-50 px-5 py-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {seats.used} of {seats.total} seat{seats.total === 1 ? "" : "s"} used
                {seats.planTitle ? ` · ${seats.planTitle} plan` : ""}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {seats.planKey
                  ? `${seats.included} included with the plan${seats.extra ? `, ${seats.extra} extra` : ""}. The owner doesn't use a seat.`
                  : "Choose a plan to add team members."}
              </p>
            </div>
            <div className="h-2 w-48 rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full bg-brand-blue-gradient"
                style={{ width: `${seats.total ? Math.min(100, (seats.used / seats.total) * 100) : 0}%` }}
              />
            </div>
          </div>
        )}

        <hr />

        {members === null ? (
          <ECSLoader />
        ) : members.length <= 1 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-400">
            <MdOutlineGroups className="text-5xl" />
            <p className="text-base font-medium">No team members yet.</p>
          </div>
        ) : null}

        {members && members.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="text-gray-400 border-b text-xs uppercase tracking-wider">
                  <th className="py-3 px-4">Member</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Seat</th>
                  <th className="py-3 px-4">Last active</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {members.map((m) => (
                  <tr key={m.userId} className="hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2.5">
                        <span
                          className={`w-2 h-2 rounded-full flex-none ${STATUS_STYLE[m.status || ""] || "bg-gray-300"}`}
                          title={m.status || ""}
                        />
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 truncate">{m.fullName || "—"}</p>
                          <p className="text-xs text-gray-400 truncate">{[m.email, m.mobile].filter(Boolean).join(" · ")}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {m.role === "OWNER" || !isOwner ? (
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${ROLE_STYLE[m.role]}`}>
                          {m.role === "OWNER" ? "Owner" : m.role === "ADMIN" ? "Admin" : "Agent"}
                        </span>
                      ) : (
                        <select
                          className="rounded-lg border border-gray-200 px-2 py-1 text-xs font-semibold"
                          value={m.role}
                          disabled={busyId === m.userId}
                          onChange={(e) => setRole(m, e.target.value)}
                          aria-label={`Role of ${m.fullName}`}
                        >
                          <option value="ADMIN">Admin</option>
                          <option value="AGENT">Agent</option>
                        </select>
                      )}
                    </td>
                    <td className="py-4 px-4 text-gray-600">
                      {m.role === "OWNER" ? "—" : (
                        <span className={m.seat === "No seat" ? "text-red-500 font-semibold" : ""}>{m.seat}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-gray-500">
                      {m.status === "INVITED" ? "Not signed in yet" : since(m.lastSeenAt)}
                    </td>
                    <td className="py-4 px-4 text-right whitespace-nowrap">
                      {m.role !== "OWNER" && (
                        <>
                          <button
                            className="text-brand-main font-semibold hover:underline text-sm disabled:opacity-50"
                            disabled={busyId === m.userId}
                            onClick={() => resetPassword(m)}
                          >
                            Reset password
                          </button>
                          <button
                            className="ml-4 text-red-500 font-semibold hover:underline text-sm disabled:opacity-50"
                            disabled={busyId === m.userId}
                            onClick={() => remove(m)}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding && (
        <AddMemberDialog
          seats={seats}
          canMakeAdmin={isOwner}
          onClose={() => setAdding(false)}
          onAdded={async (m) => {
            setAdding(false);
            if (m.temporaryPassword) setShown({ member: m, password: m.temporaryPassword });
            else toast.success(`${m.fullName || "Member"} added to your team`);
            await load();
          }}
        />
      )}

      {shown && <PasswordDialog member={shown.member} password={shown.password} onClose={() => setShown(null)} />}
    </PageLayout>
  );
}

function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[1000] grid place-items-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg hover:bg-gray-100">
            <IoClose className="text-xl" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AddMemberDialog({
  seats,
  canMakeAdmin,
  onClose,
  onAdded,
}: {
  seats: Seats | null;
  canMakeAdmin: boolean;
  onClose: () => void;
  onAdded: (m: Member) => void;
}) {
  const [mode, setMode] = useState<"create" | "existing">("create");
  const [form, setForm] = useState({ fullName: "", email: "", mobile: "", password: "", role: "AGENT" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needSeat, setNeedSeat] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  async function submit(payFrom?: "WALLET") {
    setBusy(true);
    setError(null);
    try {
      let res;
      if (mode === "create") {
        res = await customFetch.post("/api/team/agents", { ...form, payFrom });
      } else {
        const body = { email: form.email || undefined, mobile: form.email ? undefined : form.mobile, role: form.role, payFrom };
        res = await customFetch.post(payFrom ? "/api/team/employees/with-seat" : "/api/team/employees", body);
      }
      onAdded(res.data);
    } catch (e) {
      const err = apiError(e);
      if (err.code === NO_FREE_SEAT && seats?.canBuySeats) setNeedSeat(true);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="Add a team member" onClose={onClose}>
      <div className="flex gap-1 mb-4 border-b border-gray-100">
        {(
          [
            ["create", "Create account"],
            ["existing", "Existing user"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => {
              setMode(k);
              setError(null);
              setNeedSeat(false);
            }}
            className={`flex-1 py-2.5 text-sm font-semibold border-b-2 -mb-px ${
              mode === k ? "border-brand-main text-gray-800" : "border-transparent text-gray-400"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid gap-3">
        {mode === "create" ? (
          <>
            <input className={INPUT} placeholder="Full name" value={form.fullName} onChange={set("fullName")} />
            <input className={INPUT} placeholder="Email (they sign in with this)" type="email" value={form.email} onChange={set("email")} />
            <input className={INPUT} placeholder="Mobile with country code (optional)" value={form.mobile} onChange={set("mobile")} />
            <input
              className={INPUT}
              placeholder="Temporary password (leave empty to generate one)"
              value={form.password}
              onChange={set("password")}
            />
            <p className="text-xs text-gray-400">They'll be asked to choose their own password the first time they sign in.</p>
          </>
        ) : (
          <>
            <input className={INPUT} placeholder="Their email" type="email" value={form.email} onChange={set("email")} />
            <p className="text-xs text-gray-400 text-center">or</p>
            <input className={INPUT} placeholder="Their mobile" value={form.mobile} onChange={set("mobile")} />
            <p className="text-xs text-gray-400">They must already have signed up.</p>
          </>
        )}

        <label className="text-sm font-semibold text-gray-700">
          Role
          <select className={`${INPUT} mt-1.5`} value={form.role} onChange={set("role")}>
            <option value="AGENT">Agent — chats, contacts and sending</option>
            {canMakeAdmin && <option value="ADMIN">Admin — everything, including billing and team</option>}
          </select>
        </label>

        {error && <p className="text-sm text-red-500" role="alert">{error}</p>}

        {needSeat && seats?.extraSeatPrice ? (
          <div className="rounded-2xl bg-yellow-50 border border-yellow-100 p-4 text-sm">
            <p className="font-semibold text-yellow-800">All seats are in use.</p>
            <p className="text-yellow-700 mt-1">
              Add an extra seat for ₹{seats.extraSeatPrice}/month (+GST), paid from your wallet for the rest of this plan period.
            </p>
            <button className={`${BTN_MAIN} mt-3`} disabled={busy} onClick={() => submit("WALLET")}>
              {busy ? "Adding…" : "Buy a seat and add"}
            </button>
          </div>
        ) : null}

        <div className="flex justify-end gap-2 mt-2">
          <button className={BTN_PLAIN} onClick={onClose}>
            Cancel
          </button>
          <button className={BTN_MAIN} disabled={busy} onClick={() => submit()}>
            {busy ? "Adding…" : mode === "create" ? "Create and add" : "Add to team"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function PasswordDialog({ member, password, onClose }: { member: Member; password: string; onClose: () => void }) {
  const login = member.email || member.mobile || "";
  const text = `Sign in at ${window.location.origin}/login\nEmail: ${login}\nTemporary password: ${password}`;
  return (
    <Dialog title="Share these sign-in details" onClose={onClose}>
      <p className="text-sm text-gray-500 mb-3">
        This password is shown only once. Send it to {member.fullName || "the member"}; they'll choose their own password
        when they sign in.
      </p>
      <pre className="rounded-2xl bg-gray-50 border border-gray-100 p-4 text-sm whitespace-pre-wrap break-all">{text}</pre>
      <div className="flex justify-end gap-2 mt-4">
        <button
          className={BTN_PLAIN}
          onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Copied"))}
        >
          <IoCopyOutline /> Copy
        </button>
        <button className={BTN_MAIN} onClick={onClose}>
          Done
        </button>
      </div>
    </Dialog>
  );
}
